import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GroupDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import {
  GRID_COMMAND_TYPES,
  type GridCatalog,
  type GridCatalogColumn,
  type GridCommand,
  type GridCommandsDocument,
  type GridCommandsParse,
  type GridCommandType,
  type GridFieldType,
  type GridFilterGroup,
  type GridFilterLeaf,
  type GridView,
} from './grid-commands.models';

/** Operators each column type accepts (Kendo filter operator names). */
export const GRID_OPERATORS: Record<GridFieldType, readonly string[]> = {
  text: [
    'eq',
    'neq',
    'contains',
    'doesnotcontain',
    'startswith',
    'endswith',
    'isnull',
    'isnotnull',
    'isempty',
    'isnotempty',
  ],
  numeric: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'],
  boolean: ['eq', 'neq'],
};

const NO_VALUE_OPERATORS = new Set([
  'isnull',
  'isnotnull',
  'isempty',
  'isnotempty',
]);
const MAX_COMMANDS = 20;
const MAX_FILTER_LEAVES = 30;
const MAX_FILTER_DEPTH = 3;

/**
 * A refusal shown in the Ask Ryn panel. It names only catalog titles and fixed
 * wording, never seat text (field, value, operator, type or gridKey): the panel note
 * is outside AskRynAnswerPolicy, which reads the reply content only.
 */
class GridCommandError extends Error {}

const fail = (message: string): never => {
  throw new GridCommandError(message);
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function columnFor(catalog: GridCatalog, field: unknown): GridCatalogColumn {
  const column =
    typeof field === 'string'
      ? catalog.columns.find((c) => c.field === field)
      : undefined;
  return column ?? fail('The reply named a column this grid does not have.');
}

function toValue(
  column: GridCatalogColumn,
  raw: unknown
): GridFilterLeaf['value'] {
  const text = String(raw ?? '').trim();
  switch (column.type) {
    case 'numeric': {
      const n = typeof raw === 'number' ? raw : Number(text);
      return text !== '' && Number.isFinite(n)
        ? n
        : fail(`${column.title} needs a number.`);
    }
    case 'boolean':
      if (raw === true || text === 'true') return true;
      if (raw === false || text === 'false') return false;
      return fail(`${column.title} needs true or false.`);
    case 'date': {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
      const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
      return d && d.getMonth() === +m![2] - 1
        ? d
        : fail(`${column.title} needs a date as yyyy-MM-dd.`);
    }
    default:
      return text;
  }
}

function parseFilter(
  catalog: GridCatalog,
  raw: unknown,
  depth: number,
  count: { n: number }
): GridFilterGroup {
  if (
    !isRecord(raw) ||
    !Array.isArray(raw['filters']) ||
    raw['filters'].length === 0
  ) {
    return fail('A GridFilter needs { logic, filters: [...] }.');
  }
  if (depth > MAX_FILTER_DEPTH) fail('The filter is nested too deeply.');
  const logic =
    raw['logic'] === 'or'
      ? 'or'
      : raw['logic'] === 'and' || raw['logic'] == null
        ? 'and'
        : fail('Filter logic must be "and" or "or".');
  const filters = raw['filters'].map((f): GridFilterLeaf | GridFilterGroup => {
    if (isRecord(f) && Array.isArray(f['filters']))
      return parseFilter(catalog, f, depth + 1, count);
    if (!isRecord(f))
      return fail('A filter entry must be { field, operator, value }.');
    if (++count.n > MAX_FILTER_LEAVES)
      fail(`A reply may set at most ${MAX_FILTER_LEAVES} filter conditions.`);
    const column = columnFor(catalog, f['field']);
    const operator = String(f['operator'] ?? '').toLowerCase();
    if (!GRID_OPERATORS[column.type].includes(operator)) {
      fail(`That filter operator does not apply to ${column.title}.`);
    }
    if (!column.filterable) fail(`${column.title} cannot be filtered.`);
    const leaf: GridFilterLeaf = { field: column.field, operator };
    if (!NO_VALUE_OPERATORS.has(operator))
      leaf.value = toValue(column, f['value']);
    if (column.type === 'text' && f['ignoreCase'] !== false)
      leaf.ignoreCase = true;
    return leaf;
  });
  return { logic, filters };
}

function parseCommand(
  catalog: GridCatalog,
  raw: unknown,
  count: { n: number }
): GridCommand {
  const type = (isRecord(raw) ? raw['type'] : undefined) as GridCommandType;
  if (!GRID_COMMAND_TYPES.includes(type))
    fail('The reply has a grid command this page does not support.');
  const r = raw as Record<string, unknown>;
  // A command's free-text `message` is dropped here: the page never shows seat text
  // the Ask Ryn disclosure guard has not read (it checks the reply, not the action).
  const command: GridCommand = { type };
  const descriptor = (key: 'sort' | 'group') =>
    isRecord(r[key])
      ? (r[key] as Record<string, unknown>)
      : fail(`${type} needs a ${key} descriptor.`);
  const dir = (d: unknown) => (d === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc';
  switch (type) {
    case 'GridSort': {
      const s = descriptor('sort');
      const column = columnFor(catalog, s['field']);
      if (!column.sortable) fail(`${column.title} cannot be sorted.`);
      command.sort = { field: column.field, dir: dir(s['dir']) };
      break;
    }
    case 'GridGroup': {
      const g = descriptor('group');
      const column = columnFor(catalog, g['field']);
      if (!column.groupable) fail(`${column.title} cannot be grouped.`);
      command.group = { field: column.field };
      if (g['dir'] === 'desc') command.group.dir = 'desc';
      break;
    }
    case 'GridFilter':
      command.filter = parseFilter(catalog, r['filter'], 1, count);
      break;
    case 'GridColumnShow':
    case 'GridColumnHide':
      command.id = columnFor(catalog, r['id']).field;
      break;
  }
  return command;
}

/**
 * Validate a seat's `gridCommands` document against the page's catalog. All or none:
 * any bad command rejects the whole document, so the grid never ends half-changed.
 */
export function parseGridCommands(
  raw: unknown,
  catalog: GridCatalog
): GridCommandsParse {
  try {
    if (
      !isRecord(raw) ||
      !Array.isArray(raw['commands']) ||
      raw['commands'].length === 0
    ) {
      return fail('The reply has no grid commands.');
    }
    if (raw['gridKey'] !== catalog.gridKey) {
      fail('This reply is for a different grid.');
    }
    if (raw['version'] !== 1) fail('Unsupported grid command version.');
    if (raw['commands'].length > MAX_COMMANDS)
      fail(`A reply may carry at most ${MAX_COMMANDS} grid commands.`);
    const count = { n: 0 };
    const commands = raw['commands'].map((c) =>
      parseCommand(catalog, c, count)
    );
    return {
      ok: true,
      document: { version: 1, gridKey: catalog.gridKey, commands },
    };
  } catch (e) {
    if (e instanceof GridCommandError) return { ok: false, error: e.message };
    throw e;
  }
}

function filterFields(
  f: GridFilterGroup | GridFilterLeaf,
  into: Set<string>
): Set<string> {
  if ('filters' in f) f.filters.forEach((x) => filterFields(x, into));
  else into.add(f.field);
  return into;
}

/**
 * The change-chip text for one applied command, built only from the page's own
 * column titles, never from seat text or filter values.
 */
export function describeGridCommand(
  command: GridCommand,
  catalog: GridCatalog
): string {
  const title = (field: string | undefined) =>
    catalog.columns.find((c) => c.field === field)?.title ?? field ?? '';
  switch (command.type) {
    case 'GridSort':
      return `Sorted by ${title(command.sort?.field)}, ${command.sort?.dir === 'desc' ? 'descending' : 'ascending'}`;
    case 'GridFilter': {
      const fields = command.filter
        ? [...filterFields(command.filter, new Set<string>())]
        : [];
      return `Filtered on ${fields.map(title).join(', ')}`;
    }
    case 'GridGroup':
      return `Grouped by ${title(command.group?.field)}`;
    case 'GridColumnShow':
      return `Showed ${title(command.id)}`;
    case 'GridColumnHide':
      return `Hid ${title(command.id)}`;
    case 'GridClearSort':
      return 'Cleared the sort';
    case 'GridClearFilter':
      return 'Cleared the grid filters';
    case 'GridClearGroup':
      return 'Cleared the grouping';
  }
}

/** Drop every leaf on one of `fields`, and any group left empty. */
function pruneFields(
  f: CompositeFilterDescriptor | FilterDescriptor,
  fields: Set<string>
): CompositeFilterDescriptor | FilterDescriptor | null {
  if ('filters' in f) {
    const kept = f.filters
      .map((x) => pruneFields(x, fields))
      .filter(
        (x): x is CompositeFilterDescriptor | FilterDescriptor => x !== null
      );
    return kept.length ? { ...f, filters: kept } : null;
  }
  return typeof f.field === 'string' && fields.has(f.field) ? null : f;
}

/**
 * Apply validated commands, in order, to the current view. A GridFilter replaces any
 * existing condition on the same fields (so "only Estimating" swaps the stage filter)
 * and ANDs with the rest. The first GridSort in a reply replaces the sort; later ones
 * add to it. Paging always resets.
 */
export function applyGridCommands(
  view: GridView,
  document: GridCommandsDocument
): GridView {
  let filter = view.state.filter as CompositeFilterDescriptor | undefined;
  let sort: SortDescriptor[] = [...(view.state.sort ?? [])];
  let group: GroupDescriptor[] = [...(view.state.group ?? [])];
  const hidden = new Set(view.hidden);
  let sortedThisReply = false;

  for (const c of document.commands) {
    switch (c.type) {
      case 'GridSort': {
        const s = c.sort as SortDescriptor;
        sort = sortedThisReply
          ? [...sort.filter((x) => x.field !== s.field), s]
          : [s];
        sortedThisReply = true;
        break;
      }
      case 'GridClearSort':
        sort = [];
        break;
      case 'GridGroup':
        if (!group.some((g) => g.field === c.group?.field))
          group = [...group, c.group as GroupDescriptor];
        break;
      case 'GridClearGroup':
        group = [];
        break;
      case 'GridFilter': {
        const added = c.filter as CompositeFilterDescriptor;
        const rest = filter
          ? pruneFields(
              filter,
              filterFields(c.filter as GridFilterGroup, new Set())
            )
          : null;
        const restList = !rest
          ? []
          : 'filters' in rest && rest.logic === 'and'
            ? rest.filters
            : [rest];
        filter = { logic: 'and', filters: [...restList, added] };
        break;
      }
      case 'GridClearFilter':
        filter = undefined;
        break;
      case 'GridColumnHide':
        hidden.add(c.id as string);
        break;
      case 'GridColumnShow':
        hidden.delete(c.id as string);
        break;
    }
  }
  return {
    state: { ...view.state, skip: 0, sort, group, filter },
    hidden: [...hidden],
  };
}

function describeFilter(
  f: CompositeFilterDescriptor | FilterDescriptor | undefined,
  out: string[] = []
): string[] {
  if (!f) return out;
  if ('filters' in f) f.filters.forEach((x) => describeFilter(x, out));
  else
    out.push(
      `${String(f.field)} ${String(f.operator)}${f.value === undefined ? '' : ` ${f.value instanceof Date ? f.value.toISOString().slice(0, 10) : String(f.value)}`}`
    );
  return out;
}

/**
 * The page-help text Ask Ryn sends for a registered grid: the action line, the
 * current view, and every column by type. Column metadata only, never row data.
 * Kept compact because the bridge cuts page help at 2000 characters (Bug 35379);
 * the full rules live in the seat recipe (ask-ryn-responder references/grid-commands.md).
 */
export function gridCatalogText(catalog: GridCatalog, view: GridView): string {
  const list = (items: string[]) => items.join(', ') || 'none';
  const now = [
    `group ${list((view.state.group ?? []).map((g) => g.field))}`,
    `sort ${list((view.state.sort ?? []).map((s) => `${s.field} ${s.dir ?? 'asc'}`))}`,
    `filter ${list(describeFilter(view.state.filter))}`,
    `hidden ${list([...view.hidden])}`,
  ].join('; ');
  const mark = (c: GridCatalogColumn) =>
    `${c.field}=${c.title}${c.groupable ? '' : '^'}${c.sortable ? '' : '!'}${c.filterable ? '' : '~'}`;
  const byType = (Object.keys(GRID_OPERATORS) as GridFieldType[]).flatMap(
    (t) => {
      const cols = catalog.columns.filter((c) => c.type === t);
      return cols.length ? [`${t}: ${cols.map(mark).join(', ')}`] : [];
    }
  );
  return [
    `# Ask Ryn grid commands (US 35406): ${catalog.title} grid`,
    `Reply with action {kind:"gridCommands",document:{version:1,gridKey:"${catalog.gridKey}",commands:[...]}}; rules: ask-ryn-responder references/grid-commands.md. Use only these fields. If one is missing, say the grid does not have it.`,
    `Now: ${now}.`,
    'Columns by type, field=Title (^ cannot group, ! cannot sort, ~ cannot filter):',
    ...byType,
  ].join('\n');
}
